import { FormikProvider, useFormik } from 'formik'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { getFinancialProfile } from '@/selectors/user.selectors'
import { dollarsToCents } from '@/utils/currencyHelpers'
import { getFetchError } from '@/reducers/fetch'
import { useAnalyticsTrack } from '@/features/amplitude'
import {
  UPDATE_FINANCIAL_PROFILE_KEY,
  updateFinancialProfile,
} from '@/actions/financialProfileActions'

import { Alert, Text } from '@/ui'
import { useTherapistFlag, CaseStyle } from '@/utils/launchdarklyHelpers'
import {
  TAX_PROFILE_STEP,
  TaxesProfileFlowAnswerProps,
  useTaxProfileSteps,
} from './helpers'
import { TaxProfileFormFooter } from './TaxProfileFormFooter'
import { ExamplePaystub } from './ExamplePaystub'
import { W2Job } from './W2Job'
import {
  TaxProfileAccordions,
  useTaxProfileAccordionCopy,
} from './TaxProfileAccordions'
import { TypographyStyles } from '@/styles'

/*
 * This component is used for sole proprietors to enter their W-2 income outside of their practice
 * An scorp owner will see ScorpAllW2Income.tsx
 */
export const W2IncomeOutsideOfPractice = ({
  formFlowAnswers,
  setFormFlowAnswers,
}: TaxesProfileFlowAnswerProps) => {
  const dispatch = useAppDispatch()
  const track = useAnalyticsTrack()
  const fp = useReselector(getFinancialProfile)
  const error = useReselector(
    getFetchError,
    UPDATE_FINANCIAL_PROFILE_KEY(fp?.id)
  )
  const { goToPreviousStep, goToNextStep } = useTaxProfileSteps(
    TAX_PROFILE_STEP.incomeOutsideOfPractice
  )
  const replacementText = useTherapistFlag(CaseStyle.lowerCase, false)

  const someIncomeEntered =
    formFlowAnswers.wIncome !== null ||
    formFlowAnswers.estimatedAnnualW2Income !== null

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      moneyOutsideOfPractice: someIncomeEntered ? true : undefined,
      enterPaystubW2: someIncomeEntered
        ? formFlowAnswers.wIncome !== null
        : undefined,
      wIncome: formFlowAnswers.wIncome,
      estimatedAnnualW2Income: formFlowAnswers.estimatedAnnualW2Income,
    },
    onSubmit: async ({
      moneyOutsideOfPractice,
      wIncome,
      enterPaystubW2,
      estimatedAnnualW2Income,
    }) => {
      const res = await dispatch(
        updateFinancialProfile(fp?.id, {
          wIncomeInCents:
            moneyOutsideOfPractice && enterPaystubW2
              ? dollarsToCents(wIncome)
              : null,
          estimatedAnnualW2IncomeInCents:
            moneyOutsideOfPractice && !enterPaystubW2
              ? dollarsToCents(estimatedAnnualW2Income)
              : null,
          // User will skip the withholding page if answered no - clear these values in this case
          ...(!moneyOutsideOfPractice && {
            federalWithholdingInCents: null,
            stateWithholdingInCents: null,
          }),
          // If they are filling out the sole prop form, clear out the scorp values
          scorpPracticeWIncomeInCents: null,
          estimatedAnnualScorpPracticeW2IncomeInCents: null,
        })
      )

      if (res) {
        const newAnswers = {
          ...formFlowAnswers,
          wIncome,
          estimatedAnnualW2Income,
        }

        if (moneyOutsideOfPractice) {
          if (enterPaystubW2) {
            newAnswers.estimatedAnnualW2Income = null
          } else {
            newAnswers.wIncome = null
          }
        } else {
          newAnswers.wIncome = null
          newAnswers.estimatedAnnualW2Income = null
          newAnswers.federalWithholding = null
          newAnswers.stateWithholding = null
        }

        setFormFlowAnswers(newAnswers)
        track('self w2 income submitted', {
          has_w2_income_outside_practice: moneyOutsideOfPractice ?? false,
          enter_w2_paystub: Boolean(enterPaystubW2),
          w2_income_in_cents:
            moneyOutsideOfPractice && enterPaystubW2
              ? dollarsToCents(wIncome)
              : null,
          estimated_annual_w2_income_in_cents:
            moneyOutsideOfPractice && !enterPaystubW2
              ? dollarsToCents(estimatedAnnualW2Income)
              : null,
          user_id: fp?.userId ?? null,
        })
        goToNextStep(res)
      }
    },
  })

  const { isValid, isSubmitting, submitForm } = formik

  const faqCopy = useTaxProfileAccordionCopy()

  return (
    <FormikProvider value={formik}>
      <div className="w-full space-y-6">
        <ExamplePaystub zoomTo="grossIncome" />

        <Text
          className="max-w-3xl mx-auto text-center"
          variant={TypographyStyles.DISPLAY_SM}
        >
          Do you have a W-2 job?
        </Text>

        {/* Description */}
        <Text
          className="max-w-3xl mx-auto text-center"
          variant={TypographyStyles.BODY_LG}
        >
          This is a form of employment where the {replacementText} is considered
          an employee and receives a W-2 tax form every year to report their
          income and tax withholdings.
        </Text>

        {/* Error message */}
        {error && (
          <Alert
            className="max-w-3xl mx-auto"
            variant="error"
            description={error.message}
          />
        )}

        <W2Job isScorp={false} />

        <TaxProfileAccordions
          faqs={[
            faqCopy.whyNeedToIncludeIncomeOtherJobs,
            faqCopy.whatIsW2Job,
            faqCopy.howDoIEstimate,
            faqCopy.whatIsYearToDate,
            faqCopy.whatIsGrossIncome,
          ]}
        />

        {/* Footer */}
        <TaxProfileFormFooter
          submitOrContinue={submitForm}
          goToPreviousStep={goToPreviousStep}
          continueDisabled={!isValid || isSubmitting}
          loading={isSubmitting}
        />
      </div>
    </FormikProvider>
  )
}
